import { _t } from '@/i18n/t';
import {
  formatDate,
  getPlanName,
  isFreePlanName,
  type SubscriptionInfoType,
} from './subscriptionShared';

type StatusKind =
  | 'extendedTrial'
  | 'canceled'
  | 'pastDue'
  | 'trialing'
  | 'active';

const STATUS_LABEL_KEY: Record<StatusKind, string> = {
  extendedTrial: 'settings.subscription.status.extendedTrial',
  canceled: 'settings.subscription.status.canceled',
  pastDue: 'settings.subscription.status.pastDue',
  trialing: 'settings.subscription.status.trialing',
  active: 'settings.subscription.status.active',
};

const STATUS_BADGE_CLASS: Record<StatusKind, string> = {
  extendedTrial: 'bg-indigo-50 text-indigo-700',
  canceled: 'bg-orange-50 text-orange-700',
  pastDue: 'bg-red-50 text-red-700',
  trialing: 'bg-muted text-primary',
  active: 'bg-muted text-primary',
};

interface SubscriptionState {
  planName: string;
  isExtendedTrial: boolean;
  isCanceled: boolean;
  isPastDue: boolean;
  isPaidPlan: boolean;
  showPaymentBanner: boolean;
  showManageControls: boolean;
  statusKind: StatusKind;
  statusLabel: string;
  statusBadgeClass: string;
  billingDetail: string;
}

const resolveStatusKind = (
  info: SubscriptionInfoType | undefined,
  flags: { isExtendedTrial: boolean; isCanceled: boolean; isPastDue: boolean }
): StatusKind => {
  if (flags.isExtendedTrial) return 'extendedTrial';
  if (flags.isCanceled) return 'canceled';
  if (flags.isPastDue) return 'pastDue';
  if (info?.status === 'trialing') return 'trialing';
  return 'active';
};

const resolveBillingDetail = (
  info: SubscriptionInfoType | undefined,
  statusKind: StatusKind
): string => {
  switch (statusKind) {
    case 'extendedTrial':
      return _t('settings.subscription.detail.extendedTrialEnds', {
        date: formatDate(info?.trialEndsAt),
      });
    case 'trialing':
      return _t('settings.subscription.detail.trialEnds', {
        date: formatDate(info?.trialEndsAt),
      });
    case 'canceled':
      return _t('settings.subscription.detail.accessUntil', {
        date: formatDate(info?.scheduledChange?.effectiveAt),
      });
    case 'pastDue':
      return _t('settings.subscription.detail.pastDue');
    default:
      return info?.nextBilledAt
        ? _t('settings.subscription.detail.nextBilling', {
            date: formatDate(info.nextBilledAt),
          })
        : _t('settings.subscription.detail.freeAccess');
  }
};

export const deriveSubscriptionState = (
  info?: SubscriptionInfoType
): SubscriptionState => {
  const planName = getPlanName(info);
  const isExtendedTrial = !!info?.isExtendedTrial;
  const isCanceled =
    info?.status === 'active' && info?.scheduledChange?.action === 'cancel';
  const isPastDue = info?.status === 'past_due';
  const isPaidPlan = !!info?.id && !isFreePlanName(planName) && !isExtendedTrial;
  const showPaymentBanner = isPastDue && !!info?.hasUnpaidInvoice;
  const showManageControls = isPaidPlan || isPastDue;

  const statusKind = resolveStatusKind(info, {
    isExtendedTrial,
    isCanceled,
    isPastDue,
  });

  return {
    planName,
    isExtendedTrial,
    isCanceled,
    isPastDue,
    isPaidPlan,
    showPaymentBanner,
    showManageControls,
    statusKind,
    statusLabel: _t(STATUS_LABEL_KEY[statusKind]),
    statusBadgeClass: STATUS_BADGE_CLASS[statusKind],
    billingDetail: resolveBillingDetail(info, statusKind),
  };
};